import React, { useEffect, useRef, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  startAt,
  endAt,
  limit as qLimit,
  getDocs,
  doc,
  getDoc,
  documentId,
  onSnapshot,
} from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { History, Bell, X } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const MODES = [
  { value: "all", label: "All users" },
  { value: "emails", label: "Specific emails" },
];

const EmailChip = ({ email, hasTokens, onRemove }) => (
  <motion.div
    layout
    initial={{ scale: 0.9, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 0.9, opacity: 0 }}
    className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs border ${
      hasTokens
        ? "bg-green-50 border-green-200 text-green-800"
        : "bg-yellow-50 border-yellow-200 text-yellow-800"
    }`}
    title={hasTokens ? "Has push notifications enabled" : "No token registered"}
  >
    <span className="truncate max-w-[180px]">{email}</span>
    <button
      onClick={onRemove}
      className="rounded-full px-1.5 py-0.5 bg-white/70 hover:bg-white text-gray-600 border border-gray-200"
    >
      ×
    </button>
  </motion.div>
);

const SuggestionRow = ({ email, hasTokens, onSelect }) => (
  <button
    onClick={onSelect}
    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
  >
    <div className="truncate pr-2">{email}</div>
    <span
      className={`ml-3 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] border ${
        hasTokens
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-yellow-50 border-yellow-200 text-yellow-700"
      }`}
    >
      {hasTokens ? "Has token" : "No token"}
    </span>
  </button>
);

const formatDate = (d) => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return d?.toString?.() || "";
  }
};

const SendNotifications = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mode, setMode] = useState("all");

  // Autocomplete
  const [emailInput, setEmailInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [openSuggest, setOpenSuggest] = useState(false);
  const suggestBoxRef = useRef(null);

  // Send result
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // History drawer
  const [openHistory, setOpenHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState([]); // {id, title, body, recipientEmail, sentAt}
  const [loadingHistory, setLoadingHistory] = useState(false);

  const db = getFirestore();

  const isValid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (mode === "all" || (mode === "emails" && selected.length > 0));

  // Fetch suggestions (prefix search on docId = email)
  const fetchSuggestions = async (term) => {
    if (!term) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggest(true);
    try {
      const t = term.toLowerCase();
      const qRef = query(
        collection(db, "userData"),
        orderBy(documentId()),
        startAt(t),
        endAt(t + "\uf8ff"),
        qLimit(8)
      );
      const snap = await getDocs(qRef);
      const rows = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        const arr = Array.isArray(data.fcmTokens) ? data.fcmTokens : [];
        const hasTokens = arr.filter(Boolean).length > 0;
        rows.push({ email: d.id, hasTokens });
      });
      setSuggestions(rows);
      setOpenSuggest(true);
    } catch (e) {
      // toast.error("Error fetching emails");
    } finally {
      setLoadingSuggest(false);
    }
  };

  useEffect(() => {
    if (mode !== "emails") return;
    const id = setTimeout(() => fetchSuggestions(emailInput.trim()), 250);
    return () => clearTimeout(id);
  }, [emailInput, mode]);

  // Close suggestions on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!suggestBoxRef.current) return;
      if (!suggestBoxRef.current.contains(e.target)) setOpenSuggest(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const addEmail = async (email) => {
    if (!email) return;
    if (selected.some((s) => s.email === email)) return;

    let hasTokens = false;
    try {
      const dref = doc(db, "userData", email.toLowerCase());
      const d = await getDoc(dref);
      if (d.exists()) {
        const data = d.data() || {};
        const arr = Array.isArray(data.fcmTokens) ? data.fcmTokens : [];
        hasTokens = arr.filter(Boolean).length > 0;
      }
    } catch (_) {}
    setSelected((prev) => [...prev, { email, hasTokens }]);
    setEmailInput("");
    setSuggestions([]);
    setOpenSuggest(false);
  };

  const removeEmail = (email) => {
    setSelected((prev) => prev.filter((s) => s.email !== email));
  };

  const send = async () => {
    if (!isValid) return;

    setSending(true);
    setResult(null);

    const toastId = toast.loading("Sending notification…");

    try {
      const functions = getFunctions(undefined, "europe-west1");
      const callable = httpsCallable(functions, "sendAdminNotificationV2");

      const payload = {
        title: title.trim(),
        body: body.trim(),
        ...(mode === "emails" ? { emails: selected.map((s) => s.email) } : {}),
      };

      const res = await callable(payload);
      const data = res?.data || res;

      setResult(data);

      toast.update(toastId, {
        render: data?.ok
          ? "Notification sent and logged successfully."
          : `Error: ${data?.error || "Unknown error"}`,
        type: data?.ok ? "success" : "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
    } catch (e) {
      toast.update(toastId, {
        render: e?.message || "Error sending notification",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setSending(false);
    }
  };

  // Right drawer: subscribe to adminMessages in real-time
  useEffect(() => {
    if (!openHistory) return;
    setLoadingHistory(true);
    const qRef = query(
      collection(db, "adminMessages"),
      orderBy("sentAt", "desc"),
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const x = d.data() || {};
          // sentAt could be a Timestamp; fallback to sentAtIso or Date.now()
          let sentDate;
          if (x.sentAt?.toDate) sentDate = x.sentAt.toDate();
          else if (typeof x.sentAtIso === "string") sentDate = new Date(x.sentAtIso);
          else sentDate = new Date();
          rows.push({
            id: d.id,
            title: x.title || "",
            body: x.body || "",
            recipientEmail: x.recipientEmail || null,
            mode: x.mode || "",
            sentAt: sentDate,
          });
        });
        setHistoryItems(rows);
        setLoadingHistory(false);
      },
      () => setLoadingHistory(false)
    );
    return () => unsub();
  }, [openHistory, db]);

  const matchedUsers = result?.matchedUsers ?? 0;
  const processed = result?.processed ?? 0;

  return (
    <motion.div
      className="flex-1 flex items-center justify-center min-h-screen bg-gray-100 relative"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <ToastContainer position="top-right" />

      {/* Main card */}
      <motion.div
        layout
        className="relative max-w-3xl w-full p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
      >
        {/* Top-right history button */}
        <button
          onClick={() => setOpenHistory(true)}
          className="absolute right-4 top-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50 text-sm"
          title="View history"
        >
          <History size={16} />
          History
        </button>

        <h1 className="text-2xl font-bold mb-6 text-center">
          📲 Send push notifications
        </h1>

        <label className="block text-sm font-medium">Title</label>
        <input
          className="w-full border rounded-lg px-3 py-2 mb-3"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="E.g.: Important notice"
        />

        <label className="block text-sm font-medium">Message</label>
        <textarea
          className="w-full border rounded-lg px-3 py-2 mb-4"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your message here…"
        />

        <label className="block text-sm font-medium">Target</label>
        <select
          className="w-full border rounded-lg px-3 py-2 mb-4"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          {MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>

        {mode === "emails" && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Search email</label>

            <div className="relative" ref={suggestBoxRef}>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onFocus={() => emailInput && setOpenSuggest(true)}
                placeholder="Type to search…"
              />

              <AnimatePresence>
                {openSuggest && (suggestions.length > 0 || loadingSuggest) && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    className="absolute z-10 mt-2 w-full rounded-xl border bg-white shadow-lg overflow-hidden"
                  >
                    {loadingSuggest && (
                      <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
                    )}
                    {!loadingSuggest &&
                      suggestions.map((s) => (
                        <SuggestionRow
                          key={s.email}
                          email={s.email}
                          hasTokens={s.hasTokens}
                          onSelect={() => addEmail(s.email)}
                        />
                      ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <AnimatePresence>
                {selected.map((s) => (
                  <EmailChip
                    key={s.email}
                    email={s.email}
                    hasTokens={s.hasTokens}
                    onRemove={() => removeEmail(s.email)}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={send}
          disabled={sending || !isValid}
          className={`w-full py-2 rounded-lg text-white font-semibold transition ${
            sending || !isValid
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#5298A8] hover:opacity-90"
          }`}
        >
          {sending ? "Sending…" : "Send notification"}
        </motion.button>

        {result && (
          <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 rounded-xl border bg-gray-50"
          >
            <div className="px-4 py-3 border-b font-semibold text-sm">
              Result
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg bg-white border p-3">
                <div className="text-gray-500">Matched users</div>
                <div className="text-xl font-bold">{matchedUsers}</div>
              </div>
              <div className="rounded-lg bg-white border p-3">
                <div className="text-gray-500">Processed</div>
                <div className="text-xl font-bold">{result?.processed ?? 0}</div>
              </div>
              <div className="rounded-lg bg-white border p-3">
                <div className="text-gray-500">Status</div>
                <div
                  className={`text-sm font-semibold ${
                    result.ok ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {result.ok ? "OK" : "Error"}
                </div>
                {!result.ok && (
                  <div className="text-xs text-red-600 mt-1 break-all">
                    {result.error}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Right-side drawer: history */}
      <AnimatePresence>
        {openHistory && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpenHistory(false)}
            />
            {/* Panel */}
            <motion.aside
              className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl border-l border-gray-200 flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 24, stiffness: 260 }}
            >
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History size={18} />
                  <h3 className="font-semibold">Sent notifications</h3>
                </div>
                <button
                  onClick={() => setOpenHistory(false)}
                  className="rounded-md p-2 hover:bg-gray-100"
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loadingHistory && (
                  <div className="p-4 text-sm text-gray-500">Loading history…</div>
                )}

                {!loadingHistory && historyItems.length === 0 && (
                  <div className="p-4 text-sm text-gray-500">No notifications found.</div>
                )}

                <ul className="divide-y">
                  {historyItems.map((item) => (
                    <li key={item.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Bell className="text-[#5298A8]" size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <h4 className="font-medium truncate">{item.title || "Untitled"}</h4>
                            <span className="text-xs text-gray-500 shrink-0">
                              {formatDate(item.sentAt)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5 break-words">
                            {item.body}
                          </p>
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                            <span className="inline-flex items-center gap-1">
                              <span className="font-medium">To:</span>
                              {item.recipientEmail ? (
                                <span className="truncate max-w-[200px]">{item.recipientEmail}</span>
                              ) : (
                                <span>All users</span>
                              )}
                            </span>
                            {item.mode && (
                              <span className="inline-flex items-center rounded-full border px-2 py-0.5">
                                {item.mode}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SendNotifications;